
/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import _ from 'lodash';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {AuditChange, AuditChanges, AuditRecord, GenericObject, User} from '@ion/core/src/types/entities';
import {DateRangeType} from '@ion/core/src/types/services';
import {
  dateFormatter,
  DateType,
  isValidDateTime,
  timeFormatter
} from '@ion/core/src/utils/DateUtil';
import {
  safeJSONParse,
  safeJSONStringify
} from '@ion/core/src/utils/JSONUtils';
import {
  isObjectEmpty
} from '@ion/core/src/utils/ObjectUtil';
import {
  createRegExp
} from '@ion/core/src/utils/StringUtil';

// defines the prop definitions for audit grid record ...
export type AuditRecordType = {
  id?: number | string;
  date?: DateType;
  username?: string;
  user?: string;
  attributeType?: string;
  attributeValue?: string;
  oldValues?: string[];
  newValues?: string[];
  rowHeight?: number;
  rowTop?: number;
  changes?: AuditChange[];
};

export type showOptionsType = {
  showDifferences: boolean;
  showAuditVersion?: boolean;
};

// delimiter for attribute types and values ...
export const DELIMITER = '///';

// fields that are hidden from being displayed ...
export const RESTRICTED_FIELDS = [
  'version',
  'lastModifiedOn',
  'subscription',
  '_self',
  'permissions',
  'applications',
  'application',
  'account',
  'customerProfile'
];

// defines the prop definitions for getValues function ...
export interface GetValuesProps {
  value: string;
  position: number;
  delimiter: string;
  omit?: boolean;
  omitDelimiter?: boolean;
  isArray?: boolean;
};

// defines the prop definitions for getAttrValues function ...
export interface GetAttrValuesProps extends GetValuesProps {
  capitalize?: boolean;
  onlyDash?: boolean;
};

// defines the prop definitions for getTypes function ...
export interface GetTypesProps extends GetAttrValuesProps {
  concatValues?: string;
};

// defines the prop definitions for getAttributeValues function ...
export interface GetAttributeValuesProps {
  entity: AuditRecord;
  changes: AuditChange[];
  property: string;
  capitalize?: boolean;
  isValue?: boolean;
  showDifferences?: boolean;
};

// defines the prop definitions for mappingValues function ...
export interface MappingValuesProps {
  entity: AuditRecord;
  record: any;
  property: string;
  isValue?: boolean;
};

/**
 * Defines how an audit log requests and displays its records, allowing a
 * feature to customize the shared audit components without changing them.
 * When omitted, the audit components keep their default behavior.
 *
 * - `includeEntity` requests the audited entity along with each audit record.
 * - `transformRecords` transforms the audit records before the grid displays them.
 * - `transformChanges` transforms the audit changes before the changes dialog
 *   displays them.
 *
 * ```typescript
 *   import {AuditTransformOptions} from '@ion/dls/src/editors/audit/AuditGridUtils';
 *   ...
 *
 *   export const FEATURE_AUDIT_OPTIONS: AuditTransformOptions = {
 *     includeEntity    : true,
 *     transformRecords : records => records.map(withFeatureChanges),
 *     transformChanges : changes => changes.map(parseFeatureChange)
 *   };
 *
 *   <AuditDialog item={item}
 *     entity={Entity.vescout}
 *     transformOptions={FEATURE_AUDIT_OPTIONS} />
 *
 *   <ViewAuditChangesDialog auditRecord={auditRecord}
 *     entity={Entity.vescout}
 *     transformOptions={FEATURE_AUDIT_OPTIONS} />
 *
 * ```
 */
export type AuditTransformOptions = {
  includeEntity?: boolean;
  transformRecords?: (records: AuditRecord[]) => AuditRecord[];
  transformChanges?: (changes: AuditChanges) => GenericObject[];
};

export const DEFAULT_ROW_HEIGHT = 50;

/**
 * showAuditVersionField method helps to remove 'version' from RESTRICTED_FIELDS
 * if 'showAuditVersion' is true.
 */
export const showAuditVersionField = (restrictedFields, showAuditVersion: boolean) => {
  if (showAuditVersion) {
    return restrictedFields.filter(field => field !== 'version');
  };
  return restrictedFields;
};

/*
  * ucFirstLetter method helps to convert first letter of string to uppercase
  */
export const ucFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

/**
  * @private
  * Audit Services returns some fields with double quotes e.g.: ""2020-05-22T17:58:27.077Z""
  * that is why we have this method, once the services fixes the issue, we won't need this method anymore
  */
export const normalize = (value) => {
  return value?.replace(/\"/g, '');
};

/**
   * @private
   */
export const getLastModifiedOn = (c) => {
  return c?.length > 0 ? _.filter(c, ({fieldName}) => fieldName === 'lastModifiedOn') : null;
};

/**
   * @private
   */
export const hasChanges = (changes, showAuditVersion: boolean = false, showDifferences) => {
  let flag = false;
  const fieldsCopy       = !showDifferences ? [...RESTRICTED_FIELDS] : [];
  const restrictedFields = showAuditVersionField(fieldsCopy, showAuditVersion);
  if (changes?.length > 0) {
    changes?.forEach(i => {
      if (!_.includes(restrictedFields, i.fieldName)) {
        flag = true;
      }
    });
  }
  return flag;
};

/**
   * @private
   */
export const addSpace = (value, condition) => {
  const v = value.replace(condition, ' $1').trim();
  return v?.replace(/V B/g, 'VB');
};

export const getJSON = (v) => {
  if (v) {
    return _.startsWith(v, '{') || _.startsWith(v, '[') ? safeJSONParse(v) : v;
  }
  return v;
};

/**
   * @private
   */
export const hasLink = (value, obj) => {
  const isHref = _.startsWith(value, '{"href":') || _.startsWith(value, '[{"href":') || _.startsWith(value, '[{"rel":') || obj.href;
  const isApp  = (_.startsWith(value, '{"application":{"href":') && _.includes(obj.value, '{"features":['));
  return isHref || isApp;
};

/**
   * @private
   */
export const getLinkValue = (v, field) => {
  if (v) {
    const value = getJSON(v);
    if (value[field]) {
      return value[field];
    }
    if (Array.isArray(value)) {
      const values = _.map(value, field).toString();
      return value.length > 1 ? `[${values}]` : values;
    }
    if (value.value) {
      const _v       = getJSON(value.value);
      const features = _v.features ? _v.features : _v;
      if (Array.isArray(features)) {
        const _val = _.map(features, field).toString();
        return features.length > 1 ? _val?.replace(/,/g, DELIMITER) : _val;
      }
    }
  }
  return v;
};

/**
   * @private
   */
export const getAttrValues = ({
  value,
  position,
  delimiter,
  omit,
  capitalize,
  onlyDash,
  isArray
}: GetAttrValuesProps) => {
  if (value) {
    const toks = value?.split(DELIMITER);
    if (toks.length) {
      let str = '';
      toks.forEach((item, i) => {
        const val   = isArray ? item : item.split(delimiter)[position];
        const add   = position > 0 && i === 0;
        const value = `${onlyDash ? '' : capitalize ? ucFirstLetter(normalize(val)) : val}`;
        str += `${add ? '' : DELIMITER} ${omit ? ' ' : ' - '}${value}`;
      });
      return str;
    }
  }
  return value;
};

/**
   *
   * @private
   */
export const getValues = ({
  value,
  position,
  delimiter,
  omit,
  omitDelimiter,
  isArray
}: GetValuesProps) => {
  const _delimiter = omitDelimiter ? '' : DELIMITER;
  return value === '' ? value : ` - ${_delimiter}`.concat(getAttrValues({
    value,
    position,
    delimiter,
    omit,
    capitalize : false,
    onlyDash   : false,
    isArray
  }));
};

/**
   * @private
   */
export const getScheduleConf = (v) => {
  if (v) {
    const _value = getJSON(v);
    if (_value.length) {
      let keys = '';
      _value?.forEach(i => {
        const _keys = Object.keys(i);
        _keys?.forEach(x => {
          const v = x !== 'time' ? `${x}&${i[x]}` : (i[x].length > 0 ? `${x}&${i[x][0].startTime} - ${i[x][0].endTime}` : `${x}`);
          keys += keys.length === 0 ? v : `,${v}`;
        });
      });
      return keys?.replace(/,/g, DELIMITER);
    }
  }
  return '';
};

/**
   *
   * @private
   */
export const getTypes = ({
  value,
  concatValues,
  position,
  delimiter,
  omit,
  capitalize,
  onlyDash
}: GetTypesProps) => {
  return concatValues === '' ? concatValues : value.concat(getAttrValues({
    value : concatValues,
    position,
    delimiter,
    omit,
    capitalize,
    onlyDash
  }));
};

/**
   * @private
   */
export const getExtendedAttrValue = (v) => {
  if (v) {
    const _value     = getJSON(v);
    const restricted = '{"features":';
    if (_value.value) {
      const {value : val} = _value;
      if (_.startsWith(val, '{') && !_.startsWith(val, restricted)) {
        const value = val?.replace(/[{}]/g, '');
        return value?.replace(/,/g, DELIMITER);
      }
      return _.startsWith(val, restricted) ? ' ' : val;
    }
  }
  return '';
};

/**
   * @private
   */
export const getPorts = (v) => {
  if (v) {
    const _value = getJSON(v);
    if (_value.length) {
      let keys = '';
      _value?.forEach(i => {
        const value = JSON.stringify(i)?.replace(/[{}]/g, '');
        keys += keys.length === 0 ? value : `,${value}`;
      });
      return keys?.replace(/,/g, DELIMITER);
    }
  }
  return v;
};

/**
   * @private
   */
export const getRole = (roles, features) => {
  if (Array.isArray(features)) {
    features?.forEach(i => {
      roles.push(i.name);
      i.features && i.features.length > 0 && getRole(roles, i.features);
    });
    return roles;
  }
  return roles;
};

/**
   * @private
   */
export const getRoles = (v) => {
  if (v) {
    const roles = [];
    const value = getJSON(v);
    if (value.value) {
      const _v       = getJSON(value.value);
      const features = _v.features ? _v.features : _v;
      if (Array.isArray(features)) {
        const _roles = getRole(roles, features);
        const _val   = _roles.toString();
        return _roles && _roles.length > 1 ? _val?.replace(/,/g, DELIMITER) : _val;
      }
    }
  }
  return '';
};

/**
   * @private
   */
export const getAttributeValues = ({
  entity,
  changes,
  property,
  capitalize,
  isValue,
  showDifferences = false
}: GetAttributeValuesProps) => {
  if (changes?.length > 0 && property) {
    let value = '';
    changes?.forEach(record => {
      if (!_.includes((!showDifferences ? RESTRICTED_FIELDS : []), record.fieldName)) {
        let _value = normalize(mappingValues({entity, record, property, isValue}));
        _value     = isValidDateTime(_value) ? formatLocalDate(_value) : _value;
        _value     = capitalize ? ucFirstLetter(_value) : _value;
        value      = value.concat(`${value.length ? DELIMITER : ''}${_value.length ? _value : '\xa0'}`);
      }
    });
    return value;
  }
  return '';
};

const getNumberOfLinesForRowHeight = (record: AuditRecordType, useAttributeTypes = false) => {
  return (record[useAttributeTypes ? 'attributeType' : 'attributeValue']?.match(createRegExp(DELIMITER, 'g')) || []).length;
};

/**
   * @private
   */
export const getRowHeight = (record: AuditRecordType, isPhone, useAttributeTypes = false) => {
  const extraCount     = isPhone ? 2 : 1;
  const height         = isPhone ? 22 : 26;
  const delimiterCount = getNumberOfLinesForRowHeight(record, useAttributeTypes);
  const padding        = delimiterCount >= 15 ? -5 : isPhone ? 22 : 20;
  const rowHeight      = delimiterCount > 0
    ? ((delimiterCount + extraCount) * height) + (delimiterCount < 4 && isPhone ? padding + 5 : padding) : 0;
  return rowHeight;
};

/**
   * @private
   */
export const mappingValues = ({
  entity,
  record,
  property,
  isValue
}: MappingValuesProps) => {
  const fieldName          = record[property];
  const currentValue       = getJSON(record.newValue);
  const isExtAttr          = record.fieldName === 'extendedAttribute';
  const isAlertActionLinks = record.fieldName === 'alertActionLinks';
  const isRoles            = entity.entityType === 'role';

  // Extended Attributes Links
  if (currentValue && hasLink(fieldName, currentValue) && !isRoles && !isAlertActionLinks) {
    return getLinkValue(fieldName, isExtAttr ? 'name' :'title');
  }
  // Extended Attributes
  if (currentValue && isExtAttr && !_.includes(currentValue.value, '{"features":[')) {
    if (isValue) {
      return getValues({
        value     : getExtendedAttrValue(fieldName).trim(),
        position  : 1,
        delimiter : ':',
        omit      : true
      });
    }
    return getTypes({
      value        : fieldName,
      concatValues : getExtendedAttrValue(record.newValue).trim(),
      position     : 0,
      delimiter    : ':',
      omit         : false,
      capitalize   : true
    });
  }
  // Extended Attributes Roles
  if (currentValue && isExtAttr && isRoles && _.includes(currentValue.value, '{"features":[')) {
    if (isValue) {
      return getValues({
        value         : getRoles(fieldName).trim(),
        position      : 0,
        delimiter     : ':',
        omit          : true,
        omitDelimiter : true
      });
    }
    return getTypes({
      value        : fieldName,
      concatValues : getRoles(record.newValue).trim(),
      position     : 0,
      delimiter    : ':',
      omit         : false,
      capitalize   : true,
      onlyDash     : true
    });
  }
  // Schedule Configuration
  if (record.fieldName === 'scheduleConfigs') {
    if (isValue) {
      return getValues({
        value     : getScheduleConf(fieldName).trim(),
        position  : 1,
        delimiter : '&',
        omit      : true
      });
    }
    return getTypes({
      value        : fieldName,
      concatValues : getScheduleConf(record.newValue).trim(),
      position     : 0,
      delimiter    : '&',
      omit         : false,
      capitalize   : true
    });
  }
  // IO Profile Ports
  if (record.fieldName === 'ports') {
    if (isValue) {
      return getValues({
        value     : getPorts(fieldName).trim(),
        position  : 1,
        delimiter : ':',
        omit      : true
      });
    }
    return getTypes({
      value        : fieldName,
      concatValues : getPorts(record.newValue).trim(),
      position     : 0,
      delimiter    : ':',
      omit         : false,
      capitalize   : true
    });
  }
  // systemOfUnits
  if (record.fieldName === 'systemOfUnits') {
    if (isValue) {
      return currentValue?.value || currentValue;
    }
  }
  // Extensions
  if (record.fieldName === 'extensions') {
    if (currentValue && !isObjectEmpty(currentValue)) {
      let _properties = JSON.stringify(currentValue);
      _properties = _properties?.replace(/[{}]/g, '');

      if (isValue) {
        return getValues({
          value     : _properties?.replace(/,/g, DELIMITER),
          position  : 1,
          delimiter : ':',
          omit      : true
        });
      }
      return getTypes({
        value        : fieldName,
        concatValues : _properties?.replace(/,/g, DELIMITER),
        position     : 0,
        delimiter    : ':',
        omit         : false,
        capitalize   : true
      });
    }
    return '';
  }
  //placeHolders || triggerValues
  if (['placeHolders', 'triggerValues'].includes(record.fieldName)) {
    if (currentValue && !isObjectEmpty(currentValue)) {
      const isTrigger = record.fieldName === 'triggerValues';
      let values = JSON.stringify(isTrigger ? currentValue : Object.values(currentValue));
      values = values?.replace(/[[\]{}]/g, '');

      if (isValue) {
        return getValues({
          value     : values?.replace(/,/g, DELIMITER),
          position  : 1,
          delimiter : ':',
          omit      : true
        });
      }
      return getTypes({
        value        : fieldName,
        concatValues : values?.replace(/,/g, DELIMITER),
        position     : 0,
        delimiter    : ':',
        omit         : false,
        capitalize   : true,
        onlyDash     : !!isTrigger
      });
    }
    return '';
  }
  //alertActionLinks
  if (isAlertActionLinks) {
    if (currentValue && !isObjectEmpty(currentValue)) {
      let values = _.map(currentValue, i => i.title).join('$');
      values = values?.toString()?.replace(/[$]/g, DELIMITER);

      if (isValue) {
        return getValues({
          value         : values,
          position      : 1,
          delimiter     : ':',
          omit          : true,
          omitDelimiter : false,
          isArray       : true
        });
      }
      return getTypes({
        value        : fieldName,
        concatValues : values,
        position     : 0,
        delimiter    : ':',
        omit         : false,
        capitalize   : true,
        onlyDash     : true
      });
    }
    return '';
  }
  return fieldName;
};

const isValidChange = (change: AuditChange, showAuditVersion: boolean = false, showDifferences) => {
  const fieldsCopy       = !showDifferences ? [...RESTRICTED_FIELDS] : [];
  const restrictedFields = showAuditVersionField(fieldsCopy, showAuditVersion);
  return !_.includes(restrictedFields, change.fieldName);
};

/**
   * @private
   */
export const mappingAuditRecords = (
  items: AuditRecord[],
  isPhone = false,
  showOptions: showOptionsType
): AuditRecordType[] => {
  let records: AuditRecordType[] = [];
  let record: AuditRecordType    = {};
  const {showDifferences = false, showAuditVersion = false} = showOptions;
  let rowTop = 0;
  items?.forEach(r => {
    const {changes, auditId : rowId, createdOn, action, user} = r;
    const lastModifiedOn = changes?.length > 0 ? getLastModifiedOn(changes) : null;
    const date           = lastModifiedOn?.length ? normalize(lastModifiedOn[0].newValue) : createdOn;
    
    if (changes?.length > 0 && hasChanges(changes, showAuditVersion, showDifferences)) {
      record                = {};
      record.id             = rowId;
      record.date           = date;
      record.username       = user;
      record.changes        = changes;
      if (showDifferences) {
        const validChanges = changes.filter(change => isValidChange(change, showAuditVersion, showDifferences));
        const oldValues = [];
        const newValues = [];
        const attributes = [];
        validChanges.forEach(({oldValue, newValue, fieldName}) => {
          oldValues.push(oldValue);
          newValues.push(newValue);
          attributes.push(fieldName);
        });
        record.oldValues = oldValues;
        record.newValues = newValues;
        record.attributeType = attributes.join(DELIMITER);
        record.changes = changes;
      } else {
        record.attributeType  = addSpace(getAttributeValues({
          entity     : r,
          changes,
          property   : 'fieldName',
          capitalize : true,
          showDifferences
        }), /([A-Z])/g);
        record.attributeValue = getAttributeValues({
          entity     : r,
          changes,
          property   : 'newValue',
          capitalize : false,
          isValue    : true,
          showDifferences
        });
      }
      const rowHeight  = Math.max(getRowHeight(record, isPhone, showDifferences), DEFAULT_ROW_HEIGHT);
      record.rowHeight = rowHeight;
      record.rowTop    = rowTop;
      records          = [...records, record];
      rowTop           = rowTop + rowHeight;
    } else {
      const displayValue    = action === 'CREATE' ? action : 'Unknown';
      record                = {};
      record.id             = rowId;
      record.date           = date;
      record.username       = user;
      record.attributeType  = displayValue;
      record.attributeValue = displayValue;
      record.oldValues      = [displayValue];
      record.newValues      = [displayValue];
      record.changes        = changes?.length > 0 ? changes : null;
      record.rowHeight      = DEFAULT_ROW_HEIGHT;
      record.rowTop         = rowTop;
      records               = [...records, record];
      rowTop                = rowTop + DEFAULT_ROW_HEIGHT;
    }
  });
  return records;
};

/**
 * Utility which takes a date object and returns
 * the date in expected local date format
 *
 * ```typescript
 *   import {formatLocalDate} from './AuditGridUtils';
 *
 *   const date = new Date();
 *   formatLocalDate(date); // 03/23/2021 at 12:54 AM
 *
 * ```
 */
export const formatLocalDate = (date: DateType): string => {
  const calendar          = dateFormatter(date, UserPreferences.getSystemDateFormat());
  const hourTime          = timeFormatter(date, UserPreferences.getSystemTimeFormat());
  return `${calendar} ${translate('common:time.at', {time: hourTime})}`;
};

/**
 * Utility which takes file history and selected range and returns
 * the filtered file history based on selected range
 *
 * ```typescript
 *   import {postProcess} from './AuditGridUtils';
 *
 *   const date = new Date();
 *   postProcess(date);
 *
 * ```
 */
export const postProcess = (fileHistory: GenericObject[], selectedRange: DateRangeType): AuditRecord[] => {
  const {startDate, endDate} = selectedRange;
  return fileHistory.length ? fileHistory
    .filter(({requestTime}) => (requestTime >= startDate && requestTime <= endDate))
    .map((item) => ({
      auditId   : item.commandIdentifier,
      createdOn : item.requestTime,
      user      : '',
      changes   : [{
        fieldName : 'DeviceFiles',
        oldValue  : safeJSONStringify(item.oldDeviceFile),
        newValue  : safeJSONStringify(item.newDeviceFile)
      }]
    })) as AuditRecord[] : [];
};

/**
 * Utility which returns user's firstName & lastName or userName
 * based on user response.
 *
 * ```typescript
 *   import {getUserName} from './AuditGridUtils';
 *
 *   const user = getUserName(user);
 *
 * ```
 */
export const getUserName = (user: User) => {
  if (user) {
    const {firstName = '', lastName = '', username} = user;
    return username || `${firstName} ${lastName}`;
  }
  return '';
};
